import React, { useContext, useEffect, useState } from 'react';

import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';

import { AddDealContext } from 'src/contexts/addDealContext';
import {
    IRateCardErrorStates,
    IRateCardForm,
    IRateCardPerServiceErrorState,
    IRateCardValues
} from 'src/types/IAddDeal';
import { dateYYYYMMReg, getCurrentYearMonth, getFirstDayOfMonth, getLastDayOfMonth } from 'src/util/dateTimeUtil';
import { dateComparator } from 'src/util/grid/sort';
import { dateYyyyMmMask, emptyDateYyyyMmMaskStr } from 'src/util/inputMask';

import './RateCardModule.scss';

interface IRateCardModuleProps {
    rateCardInfo: IRateCardForm;
    setRateCardValues: React.Dispatch<React.SetStateAction<IRateCardValues>>;
    rateCardErrorState: IRateCardPerServiceErrorState;
    setRateCardErrorStates: React.Dispatch<React.SetStateAction<IRateCardErrorStates>>;
}

export const RateCardModule = ({
    rateCardInfo,
    setRateCardValues,
    rateCardErrorState,
    setRateCardErrorStates
}: IRateCardModuleProps) => {
    const { serviceToRateCardNameListObj } = React.useContext(AddDealContext);
    const [searchQuery, setSearchQuery] = useState('');

    const [startDateErrorMsg, setStartDateErrorMsg] = useState('');
    const [endDateErrorMsg, setEndDateErrorMsg] = useState('');

    // auto populate start date when rate card is selected
    useEffect(() => {
        if (rateCardInfo.contractName && !rateCardInfo.startDate) {
            handleRateCardValuesChange('startDate', getFirstDayOfMonth(getCurrentYearMonth('-')));
        }
    }, [rateCardInfo.contractName]);

    const matchedOptions = serviceToRateCardNameListObj[rateCardInfo.service]
        .map((optionStr) => JSON.parse(optionStr))
        .filter((option) => !searchQuery || option.contractName.toLowerCase().includes(searchQuery.toLowerCase()));

    const handleRateCardValuesChange = (key: string, value: string) => {
        if (key === 'contract') {
            const { contractId, contractName } = JSON.parse(value);

            setRateCardValues((prevState) => ({
                ...prevState,
                [rateCardInfo.service]: {
                    ...rateCardInfo,
                    contractId,
                    contractName
                }
            }));
        } else {
            setRateCardValues((prevState) => ({
                ...prevState,
                [rateCardInfo.service]: {
                    ...rateCardInfo,
                    [key]: value
                }
            }));
        }
    };

    const handleRateCardErrorStateChange = (key: string, value: boolean) => {
        setRateCardErrorStates((prevState) => ({
            ...prevState,
            [rateCardErrorState.service]: {
                ...rateCardErrorState,
                [key]: value
            }
        }));
    };

    const handleStartDateValueBlur = () => {
        if (rateCardInfo.startDate.startsWith(emptyDateYyyyMmMaskStr) || !dateYYYYMMReg.test(rateCardInfo.startDate)) {
            handleRateCardValuesChange('startDate', '');
            if (rateCardInfo.startDate.startsWith(emptyDateYyyyMmMaskStr)) {
                handleRateCardErrorStateChange('isStartDateInError', true);
                setStartDateErrorMsg('Start date is required');
            }
        } else if (
            dateYYYYMMReg.test(rateCardInfo.startDate) &&
            dateYYYYMMReg.test(rateCardInfo.endDate) &&
            dateComparator(rateCardInfo.endDate, rateCardInfo.startDate) <= 0
        ) {
            handleRateCardValuesChange('startDate', '');
            handleRateCardErrorStateChange('isStartDateInError', true);
            setStartDateErrorMsg('Start date must be before end date');
        } else {
            handleRateCardErrorStateChange('isStartDateInError', false);
            setStartDateErrorMsg('');
        }
    };

    const handleEndDateValueBlur = () => {
        if (
            !rateCardInfo.endDate.startsWith(emptyDateYyyyMmMaskStr) &&
            (!dateYYYYMMReg.test(rateCardInfo.endDate) ||
                dateComparator(rateCardInfo.endDate, rateCardInfo.startDate) <= 0)
        ) {
            handleRateCardValuesChange('endDate', '');
            if (dateComparator(rateCardInfo.endDate, rateCardInfo.startDate) <= 0) {
                handleRateCardErrorStateChange('isEndDateInError', true);
                setEndDateErrorMsg('End date must be after start date');
            }
        } else {
            // reset end date to be empty
            if (rateCardInfo.endDate.startsWith(emptyDateYyyyMmMaskStr)) {
                handleRateCardValuesChange('endDate', '');
            }
            handleRateCardErrorStateChange('isEndDateInError', false);
            setEndDateErrorMsg('');
        }
    };

    return (
        <div>
            <Text className="rate-card-title">{rateCardInfo.service} rate card</Text>
            <div>
                <Select
                    value={JSON.stringify({
                        contractId: rateCardInfo.contractId,
                        contractName: rateCardInfo.contractName
                    })}
                    onChange={(newValue) => handleRateCardValuesChange('contract', newValue)}
                    searchQuery={searchQuery}
                    onSearch={setSearchQuery}
                    placeholder="Select a rate card"
                    size="small"
                >
                    {!matchedOptions.length ? (
                        <Column alignmentVertical="center" spacing="300" spacingInset="600">
                            <Text alignment="center">No results</Text>
                        </Column>
                    ) : (
                        matchedOptions.map((option, index) => (
                            <SelectOption label={option.contractName} value={JSON.stringify(option)} key={index} />
                        ))
                    )}
                </Select>
            </div>
            <div className="date-input-div">
                <label>
                    <Text>Start date</Text>
                </label>
                <Input
                    className="date-input"
                    type="text"
                    size="small"
                    placeholder="yyyy-mm"
                    disabled={!rateCardInfo.contractName}
                    mask={dateYyyyMmMask}
                    value={rateCardInfo.startDate}
                    onChange={(newValue) => handleRateCardValuesChange('startDate', getFirstDayOfMonth(newValue))}
                    onBlur={handleStartDateValueBlur}
                    error={rateCardErrorState.isStartDateInError}
                    errorMessage={startDateErrorMsg}
                />
            </div>
            <div className="date-input-div">
                <label>
                    <Text>End date</Text>
                </label>
                <Input
                    className="date-input"
                    type="text"
                    size="small"
                    placeholder="yyyy-mm"
                    disabled={!rateCardInfo.contractName || !rateCardInfo.startDate}
                    value={rateCardInfo.endDate}
                    mask={dateYyyyMmMask}
                    onChange={(newValue) => handleRateCardValuesChange('endDate', getLastDayOfMonth(newValue))}
                    onBlur={handleEndDateValueBlur}
                    error={rateCardErrorState.isEndDateInError}
                    errorMessage={endDateErrorMsg}
                />
            </div>
        </div>
    );
};
